// ============================================================
// SecaoPlanejamentoCoord.js — abertura do trimestre e aprovação dos planos
//
// A coordenação faz três coisas aqui: abre o trimestre com prazo e janela de
// avaliações, acompanha quem entregou, e analisa o que chegou.
//
// O que a tela NÃO faz é escrever plano. Aprovar e preencher são papéis
// diferentes, e misturá-los faria a coordenação corrigir o texto do técnico
// em vez de devolver com observação, que é o que gera aprendizado.
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { listarPlanejamento, salvarTrimestre, resolverPlano, removerTrimestre } from "../firebase";
import { AZUL, AZUL_ESCURO, AZUL_CLARO, TEXTO, CINZA_ESC, BRANCO, PERIGO } from "../tema";
import { Card, Btn, StatusBox } from "../componentes";

function fmt(iso) {
  if (!iso) return "—";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

function periodoDaCompeticao(c) {
  const ok = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || ""));
  if (!ok(c.dataInicio)) return "";
  const dia = (i) => i.slice(8, 10);
  const mesAno = (i) => `${i.slice(5, 7)}/${i.slice(0, 4)}`;
  if (!ok(c.dataFim) || c.dataFim === c.dataInicio) return `${dia(c.dataInicio)}/${mesAno(c.dataInicio)}`;
  if (c.dataInicio.slice(0, 7) === c.dataFim.slice(0, 7)) {
    return `${dia(c.dataInicio)} a ${dia(c.dataFim)}/${mesAno(c.dataInicio)}`;
  }
  return `${dia(c.dataInicio)}/${c.dataInicio.slice(5, 7)} a ${dia(c.dataFim)}/${mesAno(c.dataFim)}`;
}

const ESTADO = {
  rascunho: { txt: "Rascunho", bg: "#F1F5F9", cor: CINZA_ESC },
  enviado: { txt: "Enviado", bg: "#DBEAFE", cor: "#1E40AF" },
  aprovado: { txt: "✓ Aprovado", bg: "#DCFCE7", cor: "#047857" },
  devolvido: { txt: "Devolvido", bg: "#FEE2E2", cor: "#991B1B" },
};

function Etiqueta({ status, situacao }) {
  // Prazo vencido sem envio é a informação que a coordenação procura ao
  // abrir a tela, e por isso vence a etiqueta de estado.
  if (situacao && situacao.nivel === "atrasado" && status !== "enviado" && status !== "aprovado") {
    return <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#FEF3C7", color: "#92400E" }}>Em atraso</span>;
  }
  const e = ESTADO[status] || ESTADO.rascunho;
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: e.bg, color: e.cor, whiteSpace: "nowrap" }}>
      {e.txt}
    </span>
  );
}

function Rotulo({ children }) {
  return (
    <div style={{ fontSize: 11, color: CINZA_ESC, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>
      {children}
    </div>
  );
}

const estiloCampo = {
  width: "100%", border: "1px solid #D1D5DB", borderRadius: 8,
  padding: "9px 11px", fontSize: 13.5, fontFamily: "inherit",
  boxSizing: "border-box", color: TEXTO, background: BRANCO,
};

const VAZIO = { nome: "", dataInicio: "", dataFim: "", prazoEnvio: "", avaliacaoInicio: "", avaliacaoFim: "" };

// Leitura do plano enviado. Somente leitura de propósito: ver não é editar.
function PlanoLido({ plano }) {
  const c = plano.conteudo || {};
  const bloco = (titulo, corpo) => corpo ? (
    <div style={{ marginBottom: 14 }}>
      <Rotulo>{titulo}</Rotulo>
      <div style={{ fontSize: 13.5, color: TEXTO, lineHeight: 1.6, whiteSpace: "pre-line" }}>{corpo}</div>
    </div>
  ) : null;

  return (
    <div>
      {bloco("Objetivo geral", c.objetivoGeral)}

      {(c.meses || []).some((m) => m && (m.foco || m.conteudos)) && (
        <div style={{ marginBottom: 14 }}>
          <Rotulo>Mês a mês</Rotulo>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            {(c.meses || []).map((m, i) => (
              <div key={i} style={{ background: "#FCFDFF", border: "1px solid #EEF2F7", borderRadius: 9, padding: "10px 12px" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: AZUL }}>{m.rotulo || `Mês ${i + 1}`}</div>
                {m.foco && <div style={{ fontSize: 13, marginTop: 4 }}>{m.foco}</div>}
                {m.conteudos && <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 4, lineHeight: 1.55 }}>{m.conteudos}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {bloco("Volume semanal", c.volumeSemanal)}
      {bloco("O que será avaliado", c.avaliacaoConteudo)}

      {(c.competicoes || []).length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Rotulo>Competições previstas</Rotulo>
          {(c.competicoes || []).map((comp, i) => (
            <div key={i} style={{ border: "1px solid #EEF2F7", borderRadius: 9, padding: "10px 12px", marginBottom: 7, background: "#FCFDFF" }}>
              <div style={{ display: "flex", gap: 9, alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={{ background: AZUL_CLARO, color: AZUL_ESCURO, fontWeight: 800, fontSize: 12, padding: "2px 9px", borderRadius: 6 }}>
                  {periodoDaCompeticao(comp)}
                </span>
                <span style={{ fontWeight: 700, fontSize: 13.5 }}>{comp.nome}</span>
                {comp.local && <span style={{ fontSize: 12.5, color: CINZA_ESC }}>· {comp.local}</span>}
              </div>
              {(comp.categorias || []).length > 0 && (
                <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 6 }}>
                  Categorias: {(comp.categorias || []).join(" · ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(c.objetivosPorAtleta || []).length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Rotulo>Objetivos de atletas específicos</Rotulo>
          {(c.objetivosPorAtleta || []).map((o, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 5 }}>
              <b>{o.atletaNome}</b> <span style={{ color: CINZA_ESC }}>· {o.objetivo}</span>
            </div>
          ))}
        </div>
      )}

      {bloco("Observações", c.observacoes)}

      <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 10 }}>
        {plano.enviadoPorNome ? `Enviado por ${plano.enviadoPorNome}.` : ""}
      </div>
    </div>
  );
}

// `secao` decide qual parte aparece. A tela nasceu inteira numa aba só, e
// continua funcionando assim com "tudo": quebrar em abas é organização de
// menu, não motivo para duplicar a lógica de carregar e resolver plano.
//
//   visao       entregas e prazos do trimestre
//   ciclos      abertura e edição do trimestre
//   tecnico     planos técnicos, por modalidade
//   fisico      planos de preparação física
//   aprovacoes  o que está esperando decisão da coordenação
export default function SecaoPlanejamentoCoord({ userNucleo, secao = "tudo" }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [analisando, setAnalisando] = useState(null);   // plano aberto
  const [observacoes, setObservacoes] = useState("");
  const [resolvendo, setResolvendo] = useState(false);
  const [trimestreEscolhido, setTrimestreEscolhido] = useState("");
  const [excluindo, setExcluindo] = useState(false);

  const recarregar = useCallback(async () => {
    setErro(null);
    try {
      const r = await listarPlanejamento(userNucleo, trimestreEscolhido || undefined);
      setDados(r);
      const t = (r.trimestres || []).find((x) => x.id === r.trimestreAtivoId);
      if (t) {
        setForm({
          trimestreId: t.id, nome: t.nome || "",
          dataInicio: t.dataInicio || "", dataFim: t.dataFim || "",
          prazoEnvio: t.prazoEnvio || "",
          avaliacaoInicio: t.avaliacaoInicio || "", avaliacaoFim: t.avaliacaoFim || "",
        });
      }
    } catch (e) {
      setErro("Não foi possível carregar: " + (e.message || ""));
      setDados({ trimestres: [], planos: [], modalidades: [] });
    }
  }, [userNucleo, trimestreEscolhido]);

  useEffect(() => { recarregar(); }, [recarregar]);

  async function salvar() {
    setSalvando(true);
    setStatus(null);
    try {
      await salvarTrimestre({ ...form, nucleo: userNucleo });
      setStatus({ tipo: "ok", msg: "Trimestre salvo." });
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível salvar." });
    } finally {
      setSalvando(false);
    }
  }

  // Excluir trimestre é para o que foi aberto por engano. O servidor recusa
  // quando já existe plano enviado, porque aí o trimestre virou registro do
  // que foi combinado, e o caminho passa a ser corrigir as datas.
  async function excluir() {
    const t = (dados.trimestres || []).find((x) => x.id === form.trimestreId);
    if (!t) return;
    const rascunhos = (dados.planos || []).filter((p) => (p.status || "rascunho") === "rascunho").length;
    const aviso = rascunhos
      ? `Excluir o trimestre "${t.nome}"? Os ${rascunhos} rascunho(s) começados nele serão apagados junto.`
      : `Excluir o trimestre "${t.nome}"?`;
    if (!window.confirm(aviso)) return;

    setExcluindo(true);
    setStatus(null);
    try {
      await removerTrimestre(t.id);
      setStatus({ tipo: "ok", msg: "Trimestre excluído." });
      setTrimestreEscolhido("");
      setForm(VAZIO);
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível excluir." });
    } finally {
      setExcluindo(false);
    }
  }

  async function resolver(aprovar) {
    setResolvendo(true);
    setStatus(null);
    try {
      await resolverPlano({ planoId: analisando.id, aprovar, observacoes });
      setStatus({ tipo: "ok", msg: aprovar ? "Plano aprovado." : "Plano devolvido ao responsável." });
      setAnalisando(null);
      setObservacoes("");
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível concluir." });
    } finally {
      setResolvendo(false);
    }
  }

  function novo() {
    setForm(VAZIO);
    setTrimestreEscolhido("");
  }

  if (!dados) return <Card><div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando…</div></Card>;

  const planos = dados.planos || [];
  const entregues = planos.filter((p) => p.status === "enviado" || p.status === "aprovado").length;
  const esperados = (dados.modalidades || []).length * 2; // técnico e físico por modalidade

  const mostraCiclos = secao === "tudo" || secao === "ciclos";
  const mostraLista = secao !== "ciclos" && secao !== "visao";

  // Cada aba enxerga uma fatia da mesma lista. Aprovações mostra só o que
  // está esperando decisão, porque fila com item já resolvido deixa de ser
  // fila e vira lista.
  const planosDaSecao =
    secao === "tecnico" ? planos.filter((p) => p.tipo !== "fisico")
    : secao === "fisico" ? planos.filter((p) => p.tipo === "fisico")
    : secao === "aprovacoes" ? planos.filter((p) => p.status === "enviado")
    : planos;

  const tituloDaLista =
    secao === "tecnico" ? "Planos técnicos"
    : secao === "fisico" ? "Planos de preparação física"
    : secao === "aprovacoes" ? "Esperando sua análise"
    : "Planos do trimestre";

  const vazioDaLista =
    secao === "aprovacoes"
      ? "Nada esperando decisão no momento. O que for enviado aparece aqui."
      : "Nenhum plano começado ainda. Eles aparecem aqui conforme técnicos e preparador preenchem.";

  // O que falta entregar. Sai do cruzamento das modalidades do núcleo com os
  // planos existentes: rascunho não conta como entregue, porque rascunho é
  // trabalho começado e não compromisso assumido.
  const pendencias = [];
  (dados.modalidades || []).forEach((m) => {
    ["tecnico", "fisico"].forEach((tipo) => {
      const p = planos.find(
        (x) => x.modalidade === m && (tipo === "fisico" ? x.tipo === "fisico" : x.tipo !== "fisico")
      );
      const entregue = p && (p.status === "enviado" || p.status === "aprovado");
      if (!entregue) {
        pendencias.push({
          modalidade: m, tipo,
          situacao: !p ? "nem começou" : p.status === "devolvido" ? "devolvido para ajuste" : "em rascunho",
        });
      }
    });
  });

  const aguardando = planos.filter((p) => p.status === "enviado").length;
  const devolvidos = planos.filter((p) => p.status === "devolvido").length;
  const aprovados = planos.filter((p) => p.status === "aprovado").length;

  const trimestreAtual = (dados.trimestres || []).find(
    (t) => t.id === (trimestreEscolhido || dados.trimestreAtivoId)
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {erro && <StatusBox tipo="erro" msg={erro} />}
      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      {secao === "visao" && (
        <>
          <Card>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {trimestreAtual ? trimestreAtual.nome : "Nenhum trimestre aberto"}
            </div>
            {trimestreAtual ? (
              <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 4 }}>
                {fmt(trimestreAtual.dataInicio)} a {fmt(trimestreAtual.dataFim)}
                {trimestreAtual.prazoEnvio ? " · entrega dos planos até " + fmt(trimestreAtual.prazoEnvio) : ""}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 6, lineHeight: 1.6 }}>
                Abra o trimestre em "Ciclos e períodos" para que técnicos e preparador possam
                entregar os planos.
              </div>
            )}

            {trimestreAtual && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 14 }}>
                {[
                  ["Entregues", `${entregues} de ${esperados || "—"}`, AZUL],
                  ["Esperando análise", String(aguardando), aguardando ? "#B45309" : CINZA_ESC],
                  ["Aprovados", String(aprovados), aprovados ? "#047857" : CINZA_ESC],
                  ["Devolvidos", String(devolvidos), devolvidos ? PERIGO : CINZA_ESC],
                ].map(([rotulo, valor, cor]) => (
                  <div key={rotulo} style={{ background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ fontSize: 21, fontWeight: 800, color: cor }}>{valor}</div>
                    <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 2 }}>{rotulo}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {trimestreAtual && (
            <Card>
              <div style={{ fontWeight: 700, fontSize: 15.5 }}>O que ainda falta</div>
              {pendencias.length === 0 ? (
                <div style={{ fontSize: 13, color: "#047857", marginTop: 8 }}>
                  Tudo entregue neste trimestre.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 10 }}>
                  {pendencias.map((p) => (
                    <div key={p.modalidade + p.tipo} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
                      <span style={{ fontWeight: 700, minWidth: 120 }}>{p.modalidade}</span>
                      <span style={{ color: CINZA_ESC, minWidth: 66 }}>{p.tipo === "fisico" ? "Físico" : "Técnico"}</span>
                      <span style={{ color: CINZA_ESC }}>{p.situacao}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 12, lineHeight: 1.55 }}>
                Rascunho não conta como entregue: é trabalho começado, não compromisso assumido.
                O aviso de prazo chega a quem falta, no portal e no aplicativo.
              </div>
            </Card>
          )}
        </>
      )}

      {mostraCiclos && (
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{form.trimestreId ? "Trimestre" : "Abrir trimestre"}</div>
            <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 4, lineHeight: 1.55 }}>
              As datas valem para todas as modalidades do núcleo. O prazo é o que dispara os avisos
              a quem precisa entregar.
            </div>
          </div>
          {(dados.trimestres || []).length > 0 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select value={trimestreEscolhido || dados.trimestreAtivoId || ""}
                onChange={(e) => setTrimestreEscolhido(e.target.value)}
                style={{ ...estiloCampo, width: "auto", minWidth: 190 }}>
                {(dados.trimestres || []).map((t) => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
              <button onClick={novo} style={{ background: "none", border: "none", color: AZUL, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                + novo trimestre
              </button>
            </div>
          )}
        </div>

        <div style={{ height: 16 }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
          <div>
            <Rotulo>Nome do trimestre</Rotulo>
            <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex: 1º trimestre de 2026" style={estiloCampo} />
          </div>
          <div>
            <Rotulo>Prazo de entrega dos planos</Rotulo>
            <input type="date" value={form.prazoEnvio}
              onChange={(e) => setForm({ ...form, prazoEnvio: e.target.value })} style={estiloCampo} />
          </div>
          <div>
            <Rotulo>Início do trimestre</Rotulo>
            <input type="date" value={form.dataInicio}
              onChange={(e) => setForm({ ...form, dataInicio: e.target.value })} style={estiloCampo} />
          </div>
          <div>
            <Rotulo>Fim do trimestre</Rotulo>
            <input type="date" value={form.dataFim}
              onChange={(e) => setForm({ ...form, dataFim: e.target.value })} style={estiloCampo} />
          </div>
        </div>

        <div style={{ height: 18 }} />
        <Rotulo>Período de avaliações e testes</Rotulo>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
          <input type="date" value={form.avaliacaoInicio}
            onChange={(e) => setForm({ ...form, avaliacaoInicio: e.target.value })} style={estiloCampo} />
          <input type="date" value={form.avaliacaoFim}
            onChange={(e) => setForm({ ...form, avaliacaoFim: e.target.value })} style={estiloCampo} />
        </div>
        <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 9, lineHeight: 1.55 }}>
          Uma janela só, com responsabilidades separadas: a <b>avaliação e os testes físicos</b> são do
          preparador físico e do auxiliar dele; a <b>avaliação técnica</b> é do técnico e do auxiliar dele.
          Cada plano mostra a janela ao responsável, que descreve ali o que será avaliado.
          Pode ficar em branco agora e ser preenchida depois.
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Btn onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando…" : form.trimestreId ? "Salvar alterações" : "Abrir trimestre"}
          </Btn>
          {form.trimestreId && (
            <button onClick={excluir} disabled={excluindo} title="Excluir trimestre"
              style={{ background: "none", border: "none", borderRadius: 10, padding: "6px 8px", fontSize: 15, color: "#CBD5E1", cursor: excluindo ? "wait" : "pointer" }}>
              {excluindo ? "…" : "🗑️"}
            </button>
          )}
          <span style={{ fontSize: 11.5, color: CINZA_ESC }}>
            O trimestre só pode ser excluído enquanto nenhum plano tiver sido enviado.
          </span>
        </div>
      </Card>
      )}

      {mostraLista && (dados.trimestres || []).length > 0 && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15.5 }}>{tituloDaLista}</div>
              {trimestreAtual && (
                <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 3 }}>
                  {trimestreAtual.nome}
                  {trimestreAtual.prazoEnvio ? " · prazo de entrega " + fmt(trimestreAtual.prazoEnvio) : ""}
                </div>
              )}
            </div>
            <span style={{ fontSize: 12.5, color: CINZA_ESC, fontWeight: 700 }}>
              {entregues} de {esperados || "—"} entregues
            </span>
          </div>

          {planosDaSecao.length === 0 ? (
            <div style={{ padding: 14, background: "#F8FAFC", borderRadius: 8, fontSize: 13, color: CINZA_ESC, textAlign: "center", lineHeight: 1.6 }}>
              {vazioDaLista}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {planosDaSecao.map((p) => (
                <div key={p.id} style={{ border: "1px solid #E5E7EB", borderRadius: 10, padding: "11px 14px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5, minWidth: 120 }}>{p.modalidade}</span>
                  <span style={{ fontSize: 12.5, color: CINZA_ESC, minWidth: 66 }}>
                    {p.tipo === "fisico" ? "Físico" : "Técnico"}
                  </span>
                  <span style={{ fontSize: 12.5, color: CINZA_ESC, flex: 1, minWidth: 120 }}>
                    {p.enviadoPorNome || p.atualizadoPorNome || "—"}
                  </span>
                  <Etiqueta status={p.status} situacao={p.situacao} />
                  {p.status === "enviado" ? (
                    <Btn onClick={() => { setAnalisando(p); setObservacoes(""); }}
                      style={{ fontSize: 12.5, padding: "6px 13px" }}>Analisar</Btn>
                  ) : p.status === "aprovado" ? (
                    <button onClick={() => { setAnalisando(p); setObservacoes(""); }}
                      style={{ background: "none", border: "none", color: AZUL, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Ver</button>
                  ) : (
                    <span style={{ fontSize: 12, color: CINZA_ESC }}>
                      {p.status === "devolvido" ? "aguardando ajuste" : "em preenchimento"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {analisando && (
        <Card style={{ border: `1.5px solid ${AZUL}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 15.5 }}>
              {analisando.modalidade} · plano {analisando.tipo === "fisico" ? "físico" : "técnico"}
            </div>
            <button onClick={() => setAnalisando(null)}
              style={{ background: "none", border: "none", color: CINZA_ESC, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              Fechar
            </button>
          </div>

          <div style={{ height: 14 }} />
          <PlanoLido plano={analisando} />

          <div style={{ height: 8 }} />
          <Rotulo>Observações para quem preencheu</Rotulo>
          <textarea rows={3} value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Escreva o que precisa ser ajustado. Aparece para ele no portal e no aplicativo."
            style={estiloCampo} />

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
            {analisando.status === "enviado" && (
              <Btn onClick={() => resolver(true)} disabled={resolvendo}>
                {resolvendo ? "…" : "Aprovar"}
              </Btn>
            )}
            <button onClick={() => resolver(false)} disabled={resolvendo}
              style={{ background: BRANCO, color: PERIGO, border: `1.5px solid ${PERIGO}`, borderRadius: 9, padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
              ↩ Devolver com observações
            </button>
          </div>
          <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 9, lineHeight: 1.55 }}>
            Devolver exige observação preenchida: devolver sem dizer o motivo só gera uma ida e
            volta a mais.
          </div>
        </Card>
      )}
    </div>
  );
}
